import { Button } from "../ui/Button"
import { useTranslation } from "react-i18next"
import { Dialog } from "@/components/ui/Dialog"
import { registerUserRefreshOnTabReturn, useUserQuery } from "@/api/users-api"
import { usePixiesetPaymentPlansQuery } from "@/api/payments-api"
import { GetUserResponseBody } from "@/api/dto/users"
import { Icon } from "../icons/Icon"
import clsx from "clsx"
import { ReactNode, useState } from "react"
import { uuid } from "@/utils/uuid"
import { isGenerativeFillEnabled, isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { pixiesetURLs } from "@/utils/pixieset-urls"
import { PhotoEditorPlanResponseDTO } from "@/api/dto/pixieset-payment"
import {
	getPixiesetUpgradeTabId,
	PIXIESET_UPGRADE_RETURN_PARAM,
	PIXIESET_UPGRADE_RETURN_TO_KEY,
	setUpgradeLocalStorageItem,
} from "@/utils/pixieset-upgrade"
import {
	LegacySubscriptionsDialog,
	SubscriptionTerm as LegacySubscriptionTerm,
} from "./LegacySubscriptionsDialog"
import { popDialog } from "@/store/dialogs-state"
import { analytics } from "@/telemetry/amplitude"
import { storeCurrentPixiesetSubscriptionSnapshot } from "@/telemetry/subscription-succeeded"

export type SubscriptionTerm = LegacySubscriptionTerm

type PixiesetTerm = "yearly" | "monthly"

type SubscriptionPlan = {
	term: PixiesetTerm
	cost: {
		value: number
		currency: string
	}
	displayName: string
	stripePriceId: string
}

const mapPlans = (plans: PhotoEditorPlanResponseDTO[]): SubscriptionPlan[] => {
	const mappedPlans: SubscriptionPlan[] = []

	for (const plan of plans) {
		if (plan.general_name.toLowerCase() !== "pro") {
			continue
		}

		const term =
			plan.interval === "month" ? "monthly" : plan.interval === "year" ? "yearly" : null
		if (!term) {
			continue
		}

		mappedPlans.push({
			term,
			cost: {
				value: plan.amount * 100,
				currency: plan.currency,
			},
			displayName: plan.general_name,
			stripePriceId: plan.stripe_plan_id,
		})
	}

	return mappedPlans
}

const formatCost = (
	cost: { value: number; currency: string },
	options?: {
		maximumSignificantDigits?: number
		maximumFractionDigits?: number
	},
) => {
	const moneyFormatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: cost.currency.toUpperCase(),
		maximumSignificantDigits: options?.maximumSignificantDigits,
		maximumFractionDigits: options?.maximumFractionDigits,
	})

	return moneyFormatter.format(cost.value * 1e-2)
}

const calculateCost = (plan: SubscriptionPlan) => {
	const cost = plan.cost
	const isMonthly = plan.term === "monthly"

	const monthlyValue = isMonthly ? cost.value : Math.round(cost.value / 12)
	const yearlyValue = isMonthly ? cost.value * 12 : cost.value

	return {
		monthly: formatCost(
			{ value: monthlyValue, currency: cost.currency },
			{ maximumFractionDigits: 2 },
		),
		yearly: formatCost(
			{ value: yearlyValue, currency: cost.currency },
			{ maximumFractionDigits: 0 },
		),
	}
}

const Checkpoint: React.FC<{ content: ReactNode; color: string }> = ({ content, color }) => (
	<div className={clsx("flex flex-row items-center space-x-1", color)}>
		<Icon className="shrink-0" size={14} name="check" />
		<div className="text-sm">{content}</div>
	</div>
)

const SubscriptionCard: React.FC<{
	plan: SubscriptionPlan
	origin?: string
	user?: GetUserResponseBody
}> = ({ plan, origin, user }) => {
	const { t } = useTranslation("components")
	const cost = calculateCost(plan)
	const returnTo = window.location.pathname + window.location.search + window.location.hash
	const returnUrl = (() => {
		const url = new URL(window.location.href)
		url.searchParams.set("from", PIXIESET_UPGRADE_RETURN_PARAM)
		return url.toString()
	})()

	const featureList = [
		t("subscriptionsDialog.featurePhotoEditor"),
		t("subscriptionsDialog.featureAiEditing"),
		t("subscriptionsDialog.featureAiCulling"),
		t("subscriptionsDialog.featureAiCropMask"),
		t("subscriptionsDialog.featurePersonalStyles"),
		t("subscriptionsDialog.featurePhotoExports"),
		...(isGenerativeFillEnabled() ? [t("subscriptionsDialog.featureAiRemoval")] : []),
	]

	const planName = plan.displayName
	const planPrice =
		typeof plan.cost?.value === "number" ? Number((plan.cost.value / 100).toFixed(2)) : null

	return (
		<div className="flex flex-col space-y-4 overflow-hidden rounded-lg bg-accent-700">
			<div className="flex w-full flex-row items-center justify-center space-x-2.5 bg-accent-900 py-2 text-center font-bold">
				<div className="text-sm">{planName}</div>
			</div>

			<div className="flex h-full flex-col space-y-4 px-3 pb-6">
				<div className="my-2 flex flex-col items-center space-y-4">
					<div className="text-xxl font-semibold">
						{cost.monthly}
						<span className="text-lg">{t("subscriptionsDialog.perMonth")}</span>
					</div>
					<div className="mt-4 min-h-8 whitespace-pre-line text-center text-xs text-base-200">
						{plan.term === "yearly"
							? t("subscriptionsDialog.billedAnnually")
							: t("subscriptionsDialog.billedMonthly")}
					</div>
				</div>

				<div className="flex grow flex-col">
					{featureList.map((feature) => (
						<div key={feature}>
							<div className="my-2 border-b border-base-100/10"></div>
							<Checkpoint content={feature} color="text-base-200" />
						</div>
					))}
					<div className="my-2 border-b border-base-100/10"></div>
				</div>

				<Button
					trackingLabel="subscribe"
					className="bg-accent-600"
					onClick={() => {
						popDialog()
						storeCurrentPixiesetSubscriptionSnapshot(user)
						analytics.trackEvent({
							label: "subscription_start",
							type: "click",
							customProperties: {
								origin: origin ?? null,
								planName,
								planInterval: plan.term ?? null,
								planPrice,
							},
						})
						const cycle = plan.term === "yearly" ? "year" : "month"
						const upgradeUrl = pixiesetURLs.gallery.accountUpgrade(cycle, returnUrl)
						registerUserRefreshOnTabReturn("pixieset-upgrade-return")
						setUpgradeLocalStorageItem(
							PIXIESET_UPGRADE_RETURN_TO_KEY,
							JSON.stringify({
								returnTo,
								tabId: getPixiesetUpgradeTabId(),
								timestamp: Date.now(),
							}),
						)

						window.open(upgradeUrl, "_blank", "noopener,noreferrer")
					}}
				>
					{t("subscriptionsDialog.subscribe")}
				</Button>
			</div>
		</div>
	)
}

const PixiesetPlans: React.FC<{ selectedTerm: PixiesetTerm; origin?: string }> = ({
	selectedTerm,
	origin,
}) => {
	const plansQuery = usePixiesetPaymentPlansQuery()
	const plans = mapPlans(plansQuery.data ?? [])
	const user = useUserQuery().data

	const selectedPlans = plans.filter((plan) => plan.term === selectedTerm)

	return (
		<>
			{selectedPlans.length > 0 && (
				<div className={clsx("grid gap-4", `grid-cols-${selectedPlans.length}`)}>
					{selectedPlans.map((plan) => (
						<SubscriptionCard plan={plan} origin={origin} user={user} key={uuid()} />
					))}
				</div>
			)}
		</>
	)
}

const PixiesetSubscriptionDialog = (props: { initialTerm?: SubscriptionTerm; origin?: string }) => {
	const { t } = useTranslation("components")
	useUserQuery()
	const [selectedTerm, setSelectedTerm] = useState<PixiesetTerm>(
		props.initialTerm === "monthly" ? "monthly" : "yearly",
	)

	return (
		<Dialog trackingViewName="subscription_dialog_pixieset" contentSize="sm-h-auto">
			<div className="flex w-full flex-col space-y-4">
				<div className="flex flex-row items-center justify-center space-x-4 pb-1">
					{[
						["monthly", t("subscriptionsDialog.tabMonthly")],
						["yearly", t("subscriptionsDialog.tabYearly")],
					].map(([term, label]) => (
						<div
							className={clsx(
								selectedTerm === term
									? "bg-base-900/50 text-base-300"
									: "text-base-400",
								"rounded-full px-4 py-2 text-sm font-bold",
							)}
							key={[term, label].join("::")}
							onClick={() => setSelectedTerm(term as PixiesetTerm)}
						>
							{label}
						</div>
					))}
				</div>
				<PixiesetPlans selectedTerm={selectedTerm} origin={props.origin} />
			</div>
		</Dialog>
	)
}

export const SubscriptionsDialog = (props: { initialTerm?: SubscriptionTerm; origin?: string }) => {
	if (isPixiesetAuthEnabled()) {
		return <PixiesetSubscriptionDialog {...props} />
	}

	return <LegacySubscriptionsDialog {...props} />
}
